// "Share my win" — rendered on the victory ResultModal.
//
// Highest-converting moment for an invite share is right after a win: the
// player feels triumphant, friends are easy to ping, and the share message
// includes their personal invite code so each click can convert to a +200
// chip reward for both parties (capped at the first 10 redemptions).
import React, { useEffect, useState } from "react";
import { fetchMyInvite } from "../lib/invites";
import { getPublicUrl } from "../lib/publicUrl";
import { sfx } from "../lib/sound";
import { track } from "../lib/analytics";

const COMPOSE = (code, tournamentName) => {
  const intro = tournamentName
    ? `Just won the ${tournamentName} on Quattro 🏆`
    : `Four in a row on Quattro 🎯 Beat that.`;
  const link = typeof window !== "undefined"
    ? `${getPublicUrl()}/?invite=${encodeURIComponent(code)}`
    : "";
  return [
    intro,
    `My invite code: ${code} — claim 200 free chips when you join.`,
    link,
  ].filter(Boolean).join("\n");
};

export default function ShareWinButton({ tournamentName = null }) {
  const [code, setCode] = useState(null);
  const [state, setState] = useState(""); // "", "sharing", "shared", "copied", "failed"

  useEffect(() => {
    let live = true;
    (async () => {
      try {
        const d = await fetchMyInvite();
        if (live) setCode(d?.code || null);
      } catch {
        // No code = no share. Hidden silently below.
      }
    })();
    return () => { live = false; };
  }, []);

  if (!code) return null; // Don't render until we know the user's invite code

  const onClick = async () => {
    if (state === "sharing") return;
    setState("sharing");
    sfx.click();
    try { track("victory_share_clicked", { code, tournament: tournamentName || null }); } catch {}
    try {
      // Pre-bake the message so navigator.share gets the win text, not the
      // generic invite copy. We bypass shareInvite()'s default text by
      // constructing our own pipeline.
      const text = COMPOSE(code, tournamentName);
      let method = "none";
      try {
        if (window.Capacitor?.isNativePlatform?.()) {
          const { Share } = await import("@capacitor/share");
          await Share.share({ title: "Quattro", text, dialogTitle: "Brag a little" });
          method = "native";
        } else if (navigator.share) {
          await navigator.share({ title: "Quattro", text });
          method = "web";
        } else {
          await navigator.clipboard.writeText(text);
          method = "clipboard";
        }
      } catch {
        // User dismissed share dialog or clipboard blocked — fall through to fallback.
        method = "none";
      }
      try { track("victory_share_completed", { method, code }); } catch {}
      setState(method === "clipboard" ? "copied" :
               method === "none"      ? "failed" : "shared");
      // Auto-fade the confirmation message after a few seconds so a quick
      // second tap can re-share.
      setTimeout(() => setState((s) => s === "sharing" ? s : ""), 2400);
    } catch {
      setState("failed");
    }
  };

  const label =
    state === "sharing" ? "Opening…" :
    state === "shared"  ? "Shared! 🎉" :
    state === "copied"  ? "Copied to clipboard" :
    state === "failed"  ? "Try again" :
    "🤝 Share your win";

  return (
    <button
      type="button"
      onClick={onClick}
      disabled={state === "sharing"}
      data-testid="victory-share-btn"
      className="w-full mt-3 px-4 py-2.5 rounded-full font-display font-black uppercase tracking-wider text-sm transition-all"
      style={{
        background: "linear-gradient(120deg, #3DDC97, #FFD700)",
        color: "#0B0B0B",
        border: "1.5px solid rgba(255,215,0,0.6)",
        boxShadow: "0 0 22px rgba(61,220,151,0.45)",
        opacity: state === "sharing" ? 0.7 : 1,
      }}
    >
      {label}
    </button>
  );
}
